import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, getAccessToken, refreshSession, toApiProblem } from '@/lib/api/client'
import type {
  AssistantConversationDetail, AssistantConversationView, AssistantFeedback,
  AssistantProposalView, AssistantStreamEvent,
} from '../types'

const BASE = '/v1/assistant'

/** Same origin rule as the axios instance, restated because fetch does not share its config. */
const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api'

export function useAssistantConversations(enabled = true) {
  return useQuery({
    queryKey: ['assistant', 'conversations'],
    enabled,
    queryFn: async () =>
      (await apiClient.get<AssistantConversationView[]>(`${BASE}/conversations`)).data,
  })
}

export function useAssistantConversation(id: string | null) {
  return useQuery({
    queryKey: ['assistant', 'conversations', id],
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<AssistantConversationDetail>(`${BASE}/conversations/${id}`)).data,
  })
}

export function useAssistantConversationMutations() {
  const queryClient = useQueryClient()
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['assistant', 'conversations'] })

  return {
    feedback: useMutation({
      mutationFn: async (body: { id: string; feedback: AssistantFeedback; comment?: string }) =>
        (await apiClient.post<AssistantConversationView>(
          `${BASE}/conversations/${body.id}/feedback`,
          { feedback: body.feedback, comment: body.comment ?? null },
        )).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`${BASE}/conversations/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}

/**
 * One proposal, by id.
 *
 * <p>The stream's `proposal` frame carries only the id, the tool and the summary — enough to
 * draw a card immediately, not enough to show what would actually be written. The card asks
 * for the rest, so the arguments a person approves are the ones the server recorded rather
 * than a description the model wrote of them.
 */
export function useAssistantProposal(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['assistant', 'proposals', id],
    enabled,
    queryFn: async () =>
      (await apiClient.get<AssistantProposalView>(`${BASE}/proposals/${id}`)).data,
  })
}

export function useProposalMutations() {
  const queryClient = useQueryClient()

  // A confirmed proposal writes a real row through the same service the screen uses, so the
  // screen's cache is now stale. Which one depends on the tool, and guessing wrong shows a
  // person a leave list without the request they just confirmed — so invalidate broadly.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['leave'] })
    void queryClient.invalidateQueries({ queryKey: ['attendance'] })
    void queryClient.invalidateQueries({ queryKey: ['expense'] })
    void queryClient.invalidateQueries({ queryKey: ['helpdesk'] })
    void queryClient.invalidateQueries({ queryKey: ['approvals'] })
  }

  return {
    confirm: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<AssistantProposalView>(`${BASE}/proposals/${id}/confirm`)).data,
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<AssistantProposalView>(`${BASE}/proposals/${id}/cancel`)).data,
    }),
  }
}

/**
 * Reads one answer as it is produced.
 *
 * <p>fetch, not `EventSource`. EventSource cannot set an Authorization header, and the only
 * way to authenticate it would be a token in the query string — which puts a bearer token in
 * nginx's access log, the browser's history and any proxy in between. So the stream is read
 * by hand: a POST, a reader, and a small SSE parser.
 *
 * <p>The parser is deliberately minimal but not naive about the one thing that actually
 * breaks: a chunk boundary can fall anywhere, including the middle of a JSON payload, so
 * frames are split on the blank line that terminates them and the remainder is carried over.
 *
 * @param signal abort it to stop the stream; the server records the turn regardless.
 */
export async function streamAssistantAnswer(
  body: { conversationId: string | null; question: string },
  onEvent: (event: AssistantStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await postStream(body, signal)

  if (!response.ok || !response.body) {
    throw new Error(await describeFailure(response))
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let split = buffer.indexOf('\n\n')
    while (split !== -1) {
      emit(buffer.slice(0, split), onEvent)
      buffer = buffer.slice(split + 2)
      split = buffer.indexOf('\n\n')
    }
  }
  // A stream that ends mid-frame has nothing complete left to deliver; the turn is already
  // recorded server-side, and inventing a partial event here would show text the model
  // never finished.
}

/** POSTs, and retries once through the shared refresh if the token expired mid-session. */
async function postStream(
  body: { conversationId: string | null; question: string },
  signal?: AbortSignal,
): Promise<Response> {
  const send = (token: string | null) =>
    fetch(`${BASE_URL}${BASE}/chat`, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-Correlation-Id': crypto.randomUUID(),
        ...(document.documentElement.lang ? { 'Accept-Language': document.documentElement.lang } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    })

  const first = await send(getAccessToken())
  if (first.status !== 401) return first

  // The same single-flight refresh the axios interceptor uses, so a stream opening at the
  // same moment as an ordinary request does not start a second refresh and burn the family.
  const { accessToken } = await refreshSession()
  return send(accessToken)
}

async function describeFailure(response: Response): Promise<string> {
  try {
    const problem = JSON.parse(await response.text()) as { detail?: string; title?: string }
    return problem.detail ?? problem.title ?? `The assistant refused (${response.status}).`
  } catch {
    return response.status === 403
      ? 'The assistant is not switched on for your company.'
      : `The assistant could not be reached (${response.status}).`
  }
}

/** Turns one `event:`/`data:` frame into a typed event, ignoring anything unrecognised. */
function emit(frame: string, onEvent: (event: AssistantStreamEvent) => void) {
  let name = ''
  const data: string[] = []

  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) name = line.slice(6).trim()
    else if (line.startsWith('data:')) data.push(line.slice(5).trim())
  }
  if (!name || data.length === 0) return

  try {
    const payload = JSON.parse(data.join('\n')) as Record<string, unknown>
    onEvent({ type: name, ...payload } as AssistantStreamEvent)
  } catch {
    // A frame that is not JSON is a bug on the server, not something to show a person
    // mid-sentence. The turn's own record is where it will be visible.
  }
}

export { toApiProblem }
